import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, PauseCircle, Gauge } from "lucide-react";

const OPTIONS = [
  { key: "none", label: "Uncapped" },
  { key: "week", label: "Per week" },
  { key: "month", label: "Per month" },
];

/**
 * CapacityCard — a lender's two volume controls: a pause switch and an optional cap.
 * Over-cap desks still receive cases (flagged in the Hub), so this never silently
 * blocks deal flow. Same component for the lender's own portal and the Hub override.
 */
export function CapacityCard({ scope = "admin", lenderId, onChanged }) {
  const base = scope === "lender" ? "/lender/capacity" : `/hub/lenders/${lenderId}/capacity`;
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");

  const load = useCallback(async () => {
    try { const r = await api.get(base); setSt(r.data); setDraft(String(r.data.cap_count || "")); }
    catch { setSt(null); }
  }, [base]);
  useEffect(() => { if (scope === "lender" || lenderId) load(); }, [load, lenderId, scope]);

  const save = async (patch, msg) => {
    setBusy(true);
    try {
      const r = await api.patch(base, patch);
      setSt(r.data); setDraft(String(r.data.cap_count || ""));
      if (msg) toast.success(msg);
      onChanged?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save that"); load(); }
    finally { setBusy(false); }
  };

  if (!st) return null;

  const pickPeriod = (key) => {
    if (key === "none") return save({ cap_period: "none", cap_count: 0 }, "No cap — send everything that matches");
    const n = Math.max(1, parseInt(draft, 10) || 10);
    setDraft(String(n));
    save({ cap_period: key, cap_count: n }, `Capped at ${n} per ${key}`);
  };
  const commitCount = () => {
    const n = Math.max(0, parseInt(draft, 10) || 0);
    if (n === (st.cap_count || 0)) return;
    if (!n) return save({ cap_period: "none", cap_count: 0 }, "No cap — send everything that matches");
    save({ cap_count: n, cap_period: st.cap_period || "month" }, `Capped at ${n} per ${st.cap_period || "month"}`);
  };

  const active = st.capped ? st.cap_period : "none";
  const pct = st.capped ? Math.min(100, Math.round((st.used / Math.max(1, st.cap_count)) * 100)) : 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="capacity-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <span className="grid place-items-center size-10 rounded-xl border border-slate-200 bg-slate-50 text-slate-600">
            <Gauge className="size-4.5" />
          </span>
          <div>
            <div className="text-[15px] font-semibold text-slate-900">How much deal flow you want</div>
            <div className="text-[12px] text-slate-500">
              {scope === "lender"
                ? "Pause any time, or set a limit. Nothing is lost — we just hold off."
                : "Override the desk's own settings when you need to."}
            </div>
          </div>
        </div>
        {busy && <Loader2 className="size-4 animate-spin text-slate-400" />}
      </div>

      {/* Pause */}
      <div className={`mt-4 flex items-center justify-between gap-4 rounded-xl border p-3.5 transition-colors ${
        st.paused ? "border-[#7da9ff]/45 bg-[#7da9ff]/[0.10]" : "border-slate-200 bg-slate-50/60"}`}>
        <div className="flex items-start gap-2.5 min-w-0">
          <PauseCircle className={`size-4 mt-0.5 shrink-0 ${st.paused ? "text-[#3B63A8]" : "text-slate-400"}`} />
          <div className="min-w-0">
            <div className="text-[13.5px] font-semibold text-slate-900">
              {st.paused ? "Paused — no new cases" : "Open for new cases"}
            </div>
            <div className="text-[11.5px] text-slate-500">
              {st.paused
                ? "Switch back on whenever you're ready. Live cases carry on as normal."
                : "Matching cases arrive as soon as they're approved."}
            </div>
          </div>
        </div>
        <Switch checked={!!st.leads_paused} disabled={busy}
                onCheckedChange={(v) => save({ paused: v }, v ? "Paused — nothing new will be sent" : "Back on — cases will start arriving again")}
                data-testid="capacity-pause-switch" />
      </div>

      {/* Cap */}
      <div className="mt-3">
        <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-600">Limit on new cases</div>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {OPTIONS.map((o) => (
            <button key={o.key} type="button" disabled={busy} onClick={() => pickPeriod(o.key)}
                    className={`rounded-[10px] border px-3.5 h-9 text-[12.5px] font-semibold transition-colors ${
                      active === o.key ? "border-slate-900 bg-slate-900 text-white"
                                       : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}
                    data-testid={`capacity-cap-${o.key}`}>
              {o.label}
            </button>
          ))}
          {st.capped && (
            <div className="flex items-center gap-2 ml-1">
              <Input value={draft} inputMode="numeric" disabled={busy}
                     onChange={(e) => setDraft(e.target.value.replace(/\D/g, "").slice(0, 4))}
                     onBlur={commitCount}
                     onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                     className="w-20 h-9 text-center font-mono tabular-nums" data-testid="capacity-cap-count" />
              <span className="text-[12px] text-slate-500">cases per {st.cap_period}</span>
            </div>
          )}
        </div>
      </div>

      {/* Usage */}
      <div className="mt-4 pt-4 border-t border-slate-100" data-testid="capacity-usage">
        <div className="flex items-center justify-between text-[12.5px]">
          <span className="text-slate-500">Sent to this desk {st.period_label}</span>
          <span className="font-mono tabular-nums font-semibold text-slate-900">
            {st.used}{st.capped ? ` / ${st.cap_count}` : ""}
          </span>
        </div>
        {st.capped && (
          <>
            <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${st.at_capacity ? "bg-[#7da9ff]/[0.10]0" : "bg-slate-800"}`}
                   style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-1.5 text-[11.5px] text-slate-500">
              {st.at_capacity
                ? "At capacity — anything else we send is marked over cap so it can be looked at properly, not ignored."
                : `${st.remaining} more before you hit the limit.`}
            </div>
          </>
        )}
        {st.capacity_set_by === "connexis" && scope === "lender" && (
          <div className="mt-2 text-[11.5px] text-slate-400">Last changed by the Connexis desk.</div>
        )}
      </div>
    </div>
  );
}
